<template>
  <div class="p-4 min-h-screen bg-gray-100">
  <div class="max-w-6xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm">
    <div class="px-4 pt-4 pb-3 border-b border-gray-200">
      <h1 class="text-xl font-semibold text-gray-800 mb-1">Outreach Candidates</h1>
      <p class="text-sm text-gray-600 max-w-3xl">
        Conversations that started in Outlook and look like outreach to a company in
        Aerocle. Approve one to bring it in as a thread, and replies to it are tracked
        from then on. Reject one to leave it out; it won't be suggested again. Ignore a
        domain to stop being suggested anyone there.
      </p>
    </div>

    <div>
    <!-- Filters. Search covers what's stored: subject, who it's with, and the
         mailbox -- not the message bodies. -->
    <div class="flex items-end gap-4 flex-wrap px-4 py-3 border-b border-gray-200 text-sm">
      <label class="flex flex-col gap-1 grow min-w-[16rem]">
        <span class="text-xs text-gray-500">Search</span>
        <input
          v-model="search"
          type="search"
          placeholder="Subject, name or email address"
          class="border border-gray-300 rounded-md px-2 py-1"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">Status</span>
        <select v-model="view" class="border border-gray-300 rounded-md px-2 py-1 bg-white">
          <option v-for="option in VIEWS" :key="option.key" :value="option.key">{{ option.label }}</option>
        </select>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">Answered by us</span>
        <select v-model="answered" class="border border-gray-300 rounded-md px-2 py-1 bg-white">
          <option v-for="option in ANSWERED" :key="option.key" :value="option.key">{{ option.label }}</option>
        </select>
      </label>
      <!-- Only for someone who reviews more than one mailbox. -->
      <label v-if="mailboxes.length > 1" class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">Mailbox</span>
        <select v-model="mailboxId" class="border border-gray-300 rounded-md px-2 py-1 bg-white">
          <option value="">All</option>
          <option v-for="m in mailboxes" :key="m.id" :value="String(m.id)">
            {{ m.email_address }}
          </option>
        </select>
      </label>
      <button
        v-if="filtered"
        type="button"
        @click="resetFilters"
        class="text-xs text-gray-500 hover:underline pb-1.5"
      >Reset</button>
    </div>

    <div v-if="loading" class="text-gray-500 py-12 text-center">Loading…</div>
    <div v-else-if="!rows.length" class="text-gray-500 py-12 text-center">
      <template v-if="filtered">Nothing matches these filters.</template>
      <template v-else>
        {{ view === 'pending' ? 'Nothing waiting. Conversations will appear here as they are found.' : 'Nothing rejected.' }}
      </template>
    </div>

    <div v-else class="p-3 space-y-2">
      <!-- Acting on several at once: tick rows, then act on the ticked ones. -->
      <div class="flex items-center gap-3 flex-wrap text-sm px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
        <label class="inline-flex items-center gap-2 text-gray-600 select-none">
          <input
            type="checkbox"
            class="rounded border-gray-300"
            :checked="allSelected"
            :indeterminate.prop="selectedRows.length > 0 && !allSelected"
            :disabled="!selectable.length || bulkBusy"
            @change="toggleAll"
          />
          {{ selectedRows.length ? `${selectedRows.length} selected` : 'Select all' }}
        </label>
        <template v-if="bulkBusy === 'approving'">
          <span class="text-gray-600">Bringing in {{ bulkProgress.done }} of {{ bulkProgress.total }}…</span>
        </template>
        <template v-else-if="confirmingApprove">
          <span class="text-gray-700">
            Bring in {{ approvable.length }} conversation{{ approvable.length === 1 ? '' : 's' }} as threads?
          </span>
          <button
            type="button"
            @click="onBulkApprove"
            class="px-2 py-0.5 rounded-md text-xs border border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700"
          >Bring them in</button>
          <button
            type="button"
            @click="confirmingApprove = false"
            class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100"
          >Cancel</button>
        </template>
        <template v-else-if="selectedRows.length">
          <template v-if="view === 'pending'">
            <button
              type="button"
              :disabled="!!bulkBusy"
              @click="onBulkReject"
              class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
            >Reject {{ selectedRows.length }}</button>
            <button
              type="button"
              :disabled="!!bulkBusy || !approvable.length"
              @click="confirmingApprove = true"
              class="px-2 py-0.5 rounded-md text-xs border border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >Approve {{ approvable.length }}</button>
            <span v-if="approvable.length < selectedRows.length" class="text-xs text-amber-700">
              {{ selectedRows.length - approvable.length }} with no company chosen won't be approved
            </span>
          </template>
          <button
            v-else
            type="button"
            :disabled="!!bulkBusy"
            @click="onBulkRestore"
            class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
          >Restore {{ selectedRows.length }}</button>
          <button
            type="button"
            @click="selected.clear()"
            class="text-xs text-gray-500 hover:underline"
          >Clear</button>
        </template>
        <template v-else-if="lastBulkReject">
          <span class="text-gray-600">
            Rejected {{ lastBulkReject.length }} conversation{{ lastBulkReject.length === 1 ? '' : 's' }}.
          </span>
          <button
            type="button"
            :disabled="!!bulkBusy"
            @click="onUndoBulkReject"
            class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
          >Undo</button>
        </template>
      </div>

      <div
        v-for="c in rows"
        :key="c.id"
        class="px-4 py-3 rounded-lg border shadow-sm transition-colors"
        :class="selected.has(c.id) && !outcome[c.id] ? 'bg-indigo-50 border-indigo-400 ring-1 ring-indigo-400' : 'bg-white border-gray-200'"
      >
        <!-- Decided in this visit: say what happened, with the way back. -->
        <div v-if="outcome[c.id]" class="flex items-center justify-between gap-3 text-sm">
          <span v-if="outcome[c.id].threadId" class="text-green-700">
            Brought in {{ counterpartyLabel(c) }} as a thread.
          </span>
          <span v-else-if="outcome[c.id].rejected" class="text-gray-600">
            Rejected {{ counterpartyLabel(c) }}.
          </span>
          <span v-else-if="outcome[c.id].ignored" class="text-gray-600">
            Ignoring {{ outcome[c.id].ignored.domain }} from now on.
            <template v-if="outcome[c.id].ignored.rejectedIds.length > 1">
              {{ outcome[c.id].ignored.rejectedIds.length - 1 }} other conversation{{ outcome[c.id].ignored.rejectedIds.length === 2 ? '' : 's' }} there rejected too.
            </template>
          </span>
          <span v-else class="text-gray-600">{{ outcome[c.id].message }}</span>
          <button
            v-if="outcome[c.id].threadId"
            type="button"
            @click="openThread(outcome[c.id].threadId)"
            class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100"
          >Open thread</button>
          <button
            v-else-if="outcome[c.id].rejected"
            type="button"
            :disabled="busy[c.id]"
            @click="onRestore(c)"
            class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
          >Undo</button>
          <button
            v-else-if="outcome[c.id].ignored"
            type="button"
            :disabled="busy[c.id]"
            @click="onUndoIgnore(c)"
            class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
          >Undo</button>
        </div>

        <template v-else>
          <div class="flex items-start justify-between gap-4">
            <div class="flex items-start gap-3 min-w-0">
            <input
              type="checkbox"
              class="mt-1 rounded border-gray-300"
              :checked="selected.has(c.id)"
              :disabled="!!busy[c.id] || !!bulkBusy"
              @mousedown="(e) => e.shiftKey && e.preventDefault()"
              @click="toggleSelected(c, $event)"
            />
            <div class="min-w-0">
              <div class="text-sm text-gray-800 truncate">
                <span class="font-medium">{{ c.counterparty_name || c.counterparty_email }}</span>
                <span v-if="c.counterparty_name" class="text-gray-500"> &lt;{{ c.counterparty_email }}&gt;</span>
              </div>
              <div class="text-sm text-gray-700 truncate">{{ c.subject || '(no subject)' }}</div>
              <div class="text-xs text-gray-500 mt-0.5">
                Last message {{ formatWhen(c.last_message_at) }}
                · {{ c.message_count }} message{{ c.message_count === 1 ? '' : 's' }} seen
                <template v-if="!c.has_outbound"> · nobody has answered</template>
                <template v-if="showMailbox">
                  · {{ c.mailbox }}<template v-if="c.mailbox_owner"> ({{ c.mailbox_owner }})</template>
                </template>
              </div>
            </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                @click="togglePreview(c)"
                class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100"
              >{{ preview[c.id] && preview[c.id].open ? 'Hide' : 'Preview' }}</button>
              <template v-if="view === 'pending'">
                <button
                  type="button"
                  :disabled="busy[c.id]"
                  @click="onReject(c)"
                  class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
                >Reject</button>
                <button
                  type="button"
                  :disabled="busy[c.id]"
                  :title="`Reject this and never suggest anyone at ${domainOf(c.counterparty_email)} again`"
                  @click="onIgnoreDomain(c)"
                  class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
                >Ignore domain</button>
                <button
                  type="button"
                  :disabled="busy[c.id] || !chosenCompany(c)"
                  @click="onApprove(c)"
                  class="px-2 py-0.5 rounded-md text-xs border border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >{{ busy[c.id] === 'approving' ? 'Bringing in…' : 'Approve' }}</button>
              </template>
              <button
                v-else
                type="button"
                :disabled="busy[c.id]"
                @click="onRestore(c)"
                class="px-2 py-0.5 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
              >Restore</button>
            </div>
          </div>

          <!-- The company it will be tied to: our guess, which they can change. -->
          <div class="mt-2 text-xs flex items-center gap-2 flex-wrap">
            <span class="text-gray-400">Company:</span>
            <template v-if="chosenCompany(c)">
              <span class="font-medium text-gray-800">{{ chosenCompany(c).company_name }}</span>
              <span class="text-gray-500">
                {{ chosenCompany(c).campaign ? `· ${chosenCompany(c).campaign.campaign_name}` : '· no campaign' }}
              </span>
              <span
                v-if="isSuggested(c)"
                class="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600"
              >{{ REASON_LABELS[c.suggestion_reason] || 'suggested' }}</span>
            </template>
            <span v-else class="text-amber-700">none chosen</span>
            <button
              v-if="view === 'pending' && !picker[c.id]"
              type="button"
              @click="openPicker(c)"
              class="text-indigo-600 hover:underline"
            >Change</button>
          </div>

          <div v-if="picker[c.id]" class="mt-2 relative max-w-md">
            <input
              v-model="picker[c.id].q"
              @input="onSearchInput(c)"
              type="text"
              placeholder="Search companies by name or website"
              class="w-full border rounded-md px-2 py-1 text-sm"
            />
            <div class="mt-1 border border-gray-200 rounded-md bg-white shadow-sm max-h-60 overflow-y-auto">
              <button
                v-for="option in pickerOptions(c)"
                :key="option.id"
                type="button"
                @click="choose(c, option)"
                class="block w-full text-left px-2 py-1.5 text-sm hover:bg-indigo-50"
              >
                <span class="text-gray-800">{{ option.company_name }}</span>
                <span class="text-xs text-gray-500">
                  {{ option.campaign ? ` · ${option.campaign.campaign_name}` : '' }}{{ option.website_url ? ` · ${option.website_url}` : '' }}
                </span>
              </button>
              <div v-if="!pickerOptions(c).length" class="px-2 py-1.5 text-xs text-gray-500">
                {{ picker[c.id].q.length < 2 ? 'Type at least two characters.' : 'No companies found.' }}
              </div>
            </div>
            <button
              type="button"
              @click="closePicker(c)"
              class="mt-1 text-xs text-gray-500 hover:underline"
            >Cancel</button>
          </div>

          <!-- The conversation, read live from Outlook: Graph's short preview
               of each message, oldest first. Nothing of it is stored. -->
          <div v-if="preview[c.id] && preview[c.id].open" class="mt-3 border-t border-gray-100 pt-2">
            <div v-if="preview[c.id].loading" class="text-xs text-gray-500">Reading the conversation from Outlook…</div>
            <div v-else-if="preview[c.id].error" class="text-xs text-red-600">{{ preview[c.id].error }}</div>
            <div v-else class="space-y-2">
              <div
                v-for="(m, i) in preview[c.id].messages"
                :key="i"
                class="rounded-md border px-3 py-2"
                :class="m.direction === 'outbound' ? 'border-gray-200 bg-gray-50' : 'border-indigo-100 bg-indigo-50/40'"
              >
                <div class="flex items-baseline justify-between gap-3 text-xs">
                  <span class="text-gray-700 truncate">
                    <span class="font-medium">{{ m.from_name || m.from_email }}</span>
                    → {{ m.to.join(', ') || '—' }}
                  </span>
                  <span class="text-gray-500 whitespace-nowrap">{{ formatWhen(m.received_at) }}</span>
                </div>
                <div class="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{{ m.body_preview || '(no text)' }}</div>
                <div v-if="m.has_attachments" class="text-xs text-gray-500 mt-1">Has attachments</div>
              </div>
              <div v-if="preview[c.id].skipped" class="text-xs text-gray-500">
                {{ preview[c.id].skipped }} more message{{ preview[c.id].skipped === 1 ? '' : 's' }}
                between colleagues or not sent won't be brought in.
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div v-if="numPages > 1" class="px-4 py-3 border-t border-gray-200 flex items-center justify-center gap-3 text-sm">
      <button
        type="button"
        :disabled="page <= 1"
        @click="load(page - 1)"
        class="px-2 py-1 rounded-md border border-gray-300 bg-white disabled:opacity-50"
      >Previous</button>
      <span class="text-gray-600">Page {{ page }} of {{ numPages }}</span>
      <button
        type="button"
        :disabled="page >= numPages"
        @click="load(page + 1)"
        class="px-2 py-1 rounded-md border border-gray-300 bg-white disabled:opacity-50"
      >Next</button>
    </div>

    <!-- Domains never suggested. Removing one lets new conversations through
         again; ones it already rejected stay in Rejected. -->
    <div v-if="ignoredDomains.length" class="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
      <button
        type="button"
        @click="showIgnored = !showIgnored"
        class="text-sm text-gray-600 hover:text-gray-800"
      >{{ showIgnored ? '▾' : '▸' }} Ignored domains ({{ ignoredDomains.length }})</button>
      <ul v-if="showIgnored" class="mt-2 space-y-1">
        <li
          v-for="d in ignoredDomains"
          :key="d.id"
          class="flex items-center gap-3 text-sm"
        >
          <span class="text-gray-800">{{ d.domain }}</span>
          <span v-if="showIgnoredOwner" class="text-xs text-gray-500">{{ d.owner }}</span>
          <button
            type="button"
            @click="onRemoveIgnored(d)"
            class="text-xs text-indigo-600 hover:underline"
          >Stop ignoring</button>
        </li>
      </ul>
    </div>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import {
  getOutreachCandidates, getOutreachCandidatePreview, approveOutreachCandidate,
  rejectOutreachCandidate, restoreOutreachCandidate, searchCompaniesForCandidate,
  ignoreOutreachCandidateDomain, getIgnoredOutreachDomains, removeIgnoredOutreachDomain,
  getOutreachCandidateMailboxes,
  undoIgnoredOutreachDomain, bulkRejectOutreachCandidates, bulkRestoreOutreachCandidates,
} from '@/services/OutreachCandidate'

const store = useStore()
const router = useRouter()
const $toast = useToast()

const VIEWS = [
  { key: 'pending', label: 'Waiting' },
  { key: 'rejected', label: 'Rejected' },
]
const REASON_LABELS = {
  contact: 'matches a contact',
  crm_email: 'in its CRM emails',
  domain: 'same domain',
}

// Whether anyone here has written in the conversation.
const ANSWERED = [
  { key: 'yes', label: 'Yes' },
  { key: 'no', label: 'No' },
  { key: 'any', label: 'Either' },
]

const view = ref('pending')
const answered = ref('yes')
const mailboxId = ref('')        // '' for all; else a mailbox id, as a string
const mailboxes = ref([])
const search = ref('')
const filtered = computed(() =>
  view.value !== 'pending' || answered.value !== 'yes' || mailboxId.value !== ''
  || search.value.trim() !== '')
const rows = ref([])
const loading = ref(false)
const page = ref(1)
const numPages = ref(1)

// Per-row state, keyed by candidate id.
const busy = reactive({})       // 'approving' | 'rejecting' | 'restoring'
const outcome = reactive({})    // { threadId } | { rejected } | { message }
const preview = reactive({})    // { open, loading, error, messages, skipped, matches }
const picker = reactive({})     // { q, results }
const chosen = reactive({})     // a company picked over the suggestion

// Ticked rows, and what the toolbar is doing with them.
const selected = reactive(new Set())
const bulkBusy = ref('')                  // 'approving' | 'rejecting' | 'restoring'
const bulkProgress = reactive({ done: 0, total: 0 })
const confirmingApprove = ref(false)
const lastBulkReject = ref(null)          // ids the last Reject N rejected, for Undo

// Rows still undecided on this visit can be ticked.
const selectable = computed(() => rows.value.filter((c) => !outcome[c.id]))
// Ticked and still undecided: one decided by its own button drops out.
const selectedRows = computed(() => selectable.value.filter((c) => selected.has(c.id)))
const allSelected = computed(() =>
  selectable.value.length > 0 && selectedRows.value.length === selectable.value.length)
// Approving needs a company, so only the ticked rows that have one.
const approvable = computed(() => selectedRows.value.filter((c) => chosenCompany(c)))

const ignoredDomains = ref([])
const showIgnored = ref(false)
// Dev users see every owner's list, so say whose each is once there are several.
const showIgnoredOwner = computed(() => new Set(ignoredDomains.value.map((d) => d.owner)).size > 1)

// Which mailbox (and whose) a row is from, once rows span more than one --
// dev users see every owner's.
const showMailbox = computed(() => new Set(rows.value.map((c) => c.mailbox)).size > 1)

function token() {
  return store.state.auth.token
}

function counterpartyLabel(c) {
  return c.counterparty_name || c.counterparty_email
}

function chosenCompany(c) {
  return chosen[c.id] || c.suggested_company
}

function isSuggested(c) {
  return !chosen[c.id] && c.suggested_company
}

function formatWhen(iso) {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString() } catch (e) { return iso }
}

function errorDetail(e, fallback) {
  return e?.response?.data?.detail || fallback
}

// Each load's number; a slower, older one (typing in the search) mustn't
// overwrite a newer one.
let loadSeq = 0

async function load(toPage = 1) {
  const seq = ++loadSeq
  loading.value = true
  try {
    const params = { status: view.value, answered: answered.value, page: toPage }
    if (mailboxId.value) params.mailbox = mailboxId.value
    if (search.value.trim()) params.q = search.value.trim()
    const data = await getOutreachCandidates(token(), params)
    if (seq !== loadSeq) return
    rows.value = data.results || []
    page.value = data.page || toPage
    numPages.value = data.num_pages || 1
    for (const key of Object.keys(outcome)) delete outcome[key]
    selected.clear()
    anchorId = null
    confirmingApprove.value = false
    lastBulkReject.value = null
  } catch (e) {
    if (seq !== loadSeq) return
    rows.value = []
    $toast.error('Could not load conversations from Outlook')
  } finally {
    if (seq === loadSeq) loading.value = false
  }
}

watch([view, answered, mailboxId], () => load(1))

let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => load(1), 300)
})

function resetFilters() {
  clearTimeout(searchTimer)
  search.value = ''
  view.value = 'pending'
  answered.value = 'yes'
  mailboxId.value = ''
  // The watchers reload; a search-only reset needs the reload done here.
  load(1)
}

async function togglePreview(c) {
  const state = preview[c.id]
  if (state && state.open) {
    state.open = false
    return
  }
  if (state && state.messages) {
    state.open = true
    return
  }
  preview[c.id] = { open: true, loading: true, error: '', messages: [], skipped: 0, matches: [] }
  try {
    const data = await getOutreachCandidatePreview(c.id, token())
    Object.assign(preview[c.id], {
      loading: false, messages: data.messages, skipped: data.skipped,
      matches: data.matching_companies,
    })
  } catch (e) {
    if (e?.response?.status === 409 && e.response.data.thread_id) {
      // Already a thread: nothing left to decide.
      outcome[c.id] = { threadId: e.response.data.thread_id }
      return
    }
    Object.assign(preview[c.id], {
      loading: false, error: errorDetail(e, 'Could not read the conversation from Outlook'),
    })
  }
}

function openPicker(c) {
  picker[c.id] = { q: '', results: [] }
}

function closePicker(c) {
  delete picker[c.id]
}

// Before anything is typed, offer the other companies the address matched.
function pickerOptions(c) {
  const state = picker[c.id]
  if (!state) return []
  if (state.q.trim().length >= 2) return state.results
  return (preview[c.id] && preview[c.id].matches) || []
}

const searchTimers = {}
function onSearchInput(c) {
  clearTimeout(searchTimers[c.id])
  searchTimers[c.id] = setTimeout(async () => {
    const state = picker[c.id]
    if (!state) return
    const q = state.q.trim()
    if (q.length < 2) {
      state.results = []
      return
    }
    try {
      const results = await searchCompaniesForCandidate(token(), q)
      // A slower, older search mustn't overwrite a newer one.
      if (picker[c.id] && picker[c.id].q.trim() === q) picker[c.id].results = results
    } catch (e) {
      $toast.error('Company search failed')
    }
  }, 300)
}

function choose(c, company) {
  if (c.suggested_company && company.id === c.suggested_company.id) delete chosen[c.id]
  else chosen[c.id] = company
  closePicker(c)
}

// Bring one in. Returns null on success, else why it couldn't be.
async function approveOne(c) {
  busy[c.id] = 'approving'
  try {
    const company = chosen[c.id]
    const result = await approveOutreachCandidate(c.id, token(), company ? company.id : null)
    outcome[c.id] = { threadId: result.thread_id }
    return null
  } catch (e) {
    if (e?.response?.status === 409 && e.response.data.thread_id) {
      outcome[c.id] = { threadId: e.response.data.thread_id }
      return null
    }
    return errorDetail(e, 'Could not bring this conversation in')
  } finally {
    delete busy[c.id]
    selected.delete(c.id)
  }
}

async function onApprove(c) {
  const error = await approveOne(c)
  if (error) $toast.error(error)
}

// Each approval reads the conversation from Outlook, so they go a few at a
// time, row by row -- one that fails stays ticked and the rest carry on.
const BULK_APPROVE_AT_ONCE = 3

async function onBulkApprove() {
  confirmingApprove.value = false
  lastBulkReject.value = null
  const queue = [...approvable.value]
  bulkBusy.value = 'approving'
  Object.assign(bulkProgress, { done: 0, total: queue.length })
  const failures = []
  async function worker() {
    while (queue.length) {
      const c = queue.shift()
      const error = await approveOne(c)
      if (error) {
        failures.push(error)
        selected.add(c.id)
      }
      bulkProgress.done += 1
    }
  }
  try {
    await Promise.all(Array.from({ length: BULK_APPROVE_AT_ONCE }, worker))
  } finally {
    bulkBusy.value = ''
  }
  const broughtIn = bulkProgress.total - failures.length
  if (failures.length) {
    $toast.error(`Brought in ${broughtIn}; ${failures.length} couldn't be (still ticked): ${failures[0]}`)
  } else {
    $toast.success(`Brought in ${broughtIn} conversation${broughtIn === 1 ? '' : 's'}`)
  }
}

async function onBulkReject() {
  const ids = selectedRows.value.map((c) => c.id)
  bulkBusy.value = 'rejecting'
  try {
    const { updated_ids: rejected } = await bulkRejectOutreachCandidates(ids, token())
    for (const id of rejected) {
      outcome[id] = { rejected: true }
      selected.delete(id)
    }
    lastBulkReject.value = rejected
    if (rejected.length < ids.length) {
      $toast.info(`${ids.length - rejected.length} had already been decided and were left alone`)
    }
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not reject these conversations'))
  } finally {
    bulkBusy.value = ''
  }
}

async function onUndoBulkReject() {
  bulkBusy.value = 'restoring'
  try {
    const { updated_ids: restored } = await bulkRestoreOutreachCandidates(lastBulkReject.value, token())
    for (const id of restored) delete outcome[id]
    lastBulkReject.value = null
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not undo'))
  } finally {
    bulkBusy.value = ''
  }
}

async function onBulkRestore() {
  const ids = selectedRows.value.map((c) => c.id)
  bulkBusy.value = 'restoring'
  try {
    const { updated_ids: restored } = await bulkRestoreOutreachCandidates(ids, token())
    for (const id of restored) {
      outcome[id] = { message: 'Restored; it is waiting for you again.' }
      selected.delete(id)
    }
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not restore these conversations'))
  } finally {
    bulkBusy.value = ''
  }
}

// The row last ticked or unticked by hand, for shift-click.
let anchorId = null

// A shift-click sets every undecided row from the last one clicked to this
// one the way this one goes, like a spreadsheet or Gmail.
function toggleSelected(c, event) {
  const on = !selected.has(c.id)
  const ids = selectable.value.map((row) => row.id)
  const from = ids.indexOf(anchorId)
  const to = ids.indexOf(c.id)
  const range = event && event.shiftKey && from !== -1 && to !== -1
    ? ids.slice(Math.min(from, to), Math.max(from, to) + 1)
    : [c.id]
  for (const id of range) {
    if (on) selected.add(id)
    else selected.delete(id)
  }
  anchorId = c.id
}

function toggleAll() {
  if (allSelected.value) selected.clear()
  else for (const c of selectable.value) selected.add(c.id)
}

async function onReject(c) {
  busy[c.id] = 'rejecting'
  try {
    await rejectOutreachCandidate(c.id, token())
    outcome[c.id] = { rejected: true }
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not reject this conversation'))
  } finally {
    delete busy[c.id]
  }
}

async function onRestore(c) {
  busy[c.id] = 'restoring'
  try {
    await restoreOutreachCandidate(c.id, token())
    if (view.value === 'pending') delete outcome[c.id]
    else outcome[c.id] = { message: 'Restored; it is waiting for you again.' }
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not restore this conversation'))
  } finally {
    delete busy[c.id]
  }
}

// The thread page's Back honors ?from=, so it returns here.
function openThread(threadId) {
  router.push({ path: `/outreach-threads/${threadId}`, query: { from: '/outreach-candidates' } })
}

// The part after @, for the button's tooltip. The server works out the
// registrable domain (mail.acme.com -> acme.com) when it ignores one.
function domainOf(address) {
  return (address || '').split('@').pop()
}

async function loadIgnored() {
  try {
    ignoredDomains.value = await getIgnoredOutreachDomains(token())
  } catch (e) {
    // The list is a convenience; the page works without it.
  }
}

async function onIgnoreDomain(c) {
  busy[c.id] = 'ignoring'
  try {
    const result = await ignoreOutreachCandidateDomain(c.id, token())
    const ignored = { domain: result.domain, id: result.ignored_domain_id, rejectedIds: result.rejected_ids }
    // Other rows on this page from the same domain were rejected with it.
    for (const id of result.rejected_ids) {
      if (id !== c.id) outcome[id] = { message: `Rejected: ${result.domain} is ignored.` }
    }
    outcome[c.id] = { ignored }
    loadIgnored()
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not ignore this domain'))
  } finally {
    delete busy[c.id]
  }
}

async function onUndoIgnore(c) {
  const { ignored } = outcome[c.id]
  busy[c.id] = 'restoring'
  try {
    await undoIgnoredOutreachDomain(ignored.id, ignored.rejectedIds, token())
    for (const id of ignored.rejectedIds) delete outcome[id]
    loadIgnored()
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not undo'))
  } finally {
    delete busy[c.id]
  }
}

async function onRemoveIgnored(d) {
  try {
    await removeIgnoredOutreachDomain(d.id, token())
    loadIgnored()
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not stop ignoring this domain'))
  }
}

async function loadMailboxes() {
  try {
    mailboxes.value = await getOutreachCandidateMailboxes(token())
  } catch (e) {
    // Without it there's just no mailbox filter.
  }
}

onMounted(() => {
  load(1)
  loadIgnored()
  loadMailboxes()
})
</script>
